import { DataService } from "@/services/data";
import { MRContact } from "@/types/medrep/contact";
import { handle_error } from "@/utils/wretch";
import { Navigate, RouteObject, defer, json, redirect } from "react-router-dom";
import { toast } from "sonner";

const ContactRoute: RouteObject = {
    path: "contacts",
    children: [
        {
            index: true,
            async lazy() {
                let { ListPage } = await import("@/pages/medrep/contacts/list");
                return { Component: ListPage };
            },
            loader: async ({ request }) => {
                const url = new URL(request.url);
                if (!url.searchParams.has("page_size")) {
                    url.searchParams.set("page_size", "25");
                }
                const contacts = DataService.client
                    .url(`/medrep/contacts/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                    .get()
                    .json();
                return defer({ contacts });
            }
        },
        {
            path: "nouveau",
            action: async ({ request }) => {
                const data = await request.json();

                try {
                    const contact = (await DataService.client.url(`/medrep/contacts/`).post(data).json()) as MRContact;
                    toast.success("Le contact a été créé");
                    return redirect(`/dm/contacts/${contact.id}`);
                } catch (error) {
                    toast.error("Une erreur est survenue lors de la création du contact");
                }
                return redirect("..");
            }
        },
        {
            id: "medrep-contact-detail",
            path: ":contactId",
            async lazy() {
                let { DetailLayout } = await import("@/pages/medrep/contacts/detail-layout");
                return { Component: DetailLayout };
            },
            loader: async ({ params }) => {
                const contactId = params.contactId;
                const contact = (await DataService.client.url(`/medrep/contacts/${contactId}/`).get().json()) as MRContact;
                return defer({ contact });
            },
            action: async ({ params, request }) => {
                const contactId = params.contactId;
                const data = await request.json();
                const url = new URL(request.url);
                const action = url.searchParams.get("action");
                if (action) {
                    try {
                        const content = await DataService.client.url(`/medrep/contacts/${contactId}/${action}/`).post(data).json();
                        return json({ success: true, content: content });
                    } catch (error) {
                        return handle_error(error);
                    }
                } else {
                    try {
                        const content = await DataService.client.url(`/medrep/contacts/${contactId}/`).patch(data).json();
                        return json({ success: true, content: content });
                    } catch (error) {
                        return handle_error(error);
                    }
                }
            },
            children: [
                {
                    index: true,
                    element: <Navigate to="informations" replace />
                },
                {
                    path: "informations",
                    async lazy() {
                        let { DetailInformationsPage } = await import("@/pages/medrep/contacts/detail-informations");
                        return { Component: DetailInformationsPage };
                    }
                },
                {
                    path: "carte",
                    async lazy() {
                        let { DetailMapPage } = await import("@/pages/medrep/contacts/detail-map");
                        return { Component: DetailMapPage };
                    },
                    loader: ({ params }) => {
                        const contactId = params.contactId;
                        const contacts = DataService.client.url(`/medrep/contact_map/${contactId}/contacts/`).get().json();
                        const sobjects = DataService.client.url(`/medrep/contact_map/${contactId}/sobjects/`).get().json();
                        return defer({ contacts, sobjects });
                    }
                },
                {
                    path: "formulaires",
                    async lazy() {
                        let { DetailRegistrationsPage } = await import("@/pages/medrep/contacts/detail-registrations");
                        return { Component: DetailRegistrationsPage };
                    },
                    loader: async ({ params }) => {
                        const contactId = params.contactId;
                        const registrations = await DataService.client.url(`/medrep/registrations/?registration_type=PreConfiguredRegistration&mr_contact_id=${contactId}`).get().json();
                        return defer({ registrations });
                    }
                },
                {
                    path: "inscriptions",
                    loader: async ({ params }) => {
                        const contactId = params.contactId;
                        const cases = await DataService.client.url(`/medrep/contacts/${contactId}/cases/`).get().json();
                        return cases;
                    }
                },
                {
                    path: "catalogue",
                    loader: async ({ params }) => {
                        const contactId = params.contactId;
                        const products = await DataService.client.url(`/medrep/contacts/${contactId}/products/`).get().json();
                        return products;
                    }
                },
                {
                    path: "share",
                    loader: async ({ params }) => {
                        const contactId = params.contactId;
                        const contact_share = await DataService.client.url(`/medrep/contacts/${contactId}/share/`).get().json();
                        return contact_share;
                    },
                    action: async ({ params, request }) => {
                        const contactId = params.contactId;
                        const data = await request.json();
                        try {
                            const content = await DataService.client.url(`/medrep/contacts/${contactId}/share/`).post(data).json();
                            return json({ success: true, content: content });
                        } catch (error) {
                            return handle_error(error);
                        }
                    }
                }
            ]
        },
        {
            path: "rappels",
            async lazy() {
                let { TaskEventPage } = await import("@/pages/medrep/contacts/event");
                return { Component: TaskEventPage };
            },
            loader: async ({ request }) => {
                const { DEFAULT_DATE_RANGE } = await import("@/pages/medrep/contacts/event");
                const url = new URL(request.url);
                url.searchParams.set("type", "event");
                url.searchParams.set("activity_date", url.searchParams.get("activity_date") || DEFAULT_DATE_RANGE);
                url.searchParams.set("page_size", url.searchParams.get("page_size") || "250");
                const tasks = await DataService.client
                    .url(`/medrep/tasks/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                    .get()
                    .json();
                return defer({ tasks });
            }
        }
    ]
};

export default ContactRoute;
